import { useEffect, useMemo } from "react";
import { usePushGetHelp } from "@medbillai/app/lib/usePushGetHelp";
import { Paragraph2, Separator, XStack, YStack } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { BillHelpBadge, CoverageCheckBadge, OpenEnrollmentBadge, SomethingElseBadge } from "../../../components/local-images";
import { analyticsService } from "../../../lib/analytics";
import { isBenefitsTargeted, useAuth } from "../../../lib/auth";
import { useBoolVariation } from "../../../lib/flag";
import { useShouldShowUploadBill } from "../utils/useShouldShowUploadBill";
import { GetHelpPrompt, type GetHelpPromptProps } from "./GetHelpPrompt";
import { WidgetWrapper } from "./WidgetWrapper";
const PROMPT_TYPES = {
  BENEFITS_QUESTION: "BENEFITS_QUESTION",
  BILL_ISSUE: "BILL_ISSUE",
  OPEN_ENROLLMENT: "OPEN_ENROLLMENT",
  OTHER: "OTHER"
} as const;
type PromptType = (typeof PROMPT_TYPES)[keyof typeof PROMPT_TYPES];
type PromptConfig = GetHelpPromptProps & {
  source: string;
};
const PROMPT_CONFIGS: Record<PromptType, Omit<PromptConfig, "onPress">> = {
  [PROMPT_TYPES.BENEFITS_QUESTION]: {
    title: "Check my coverage",
    description: "Get instant, personalized answers on your insurance questions.",
    badge: <CoverageCheckBadge />,
    source: "home_get_help_prompts_benefits"
  },
  [PROMPT_TYPES.BILL_ISSUE]: {
    title: "Get help with a bill",
    description: "We check bills for overcharges/errors, then work to lower them.",
    badge: <BillHelpBadge />,
    source: "home_get_help_prompts_bill"
  },
  [PROMPT_TYPES.OPEN_ENROLLMENT]: {
    title: "Check my open enrollment",
    description: "We can help you choose your insurance for the 2026 coverage year.",
    badge: <OpenEnrollmentBadge />,
    labelBadge: <XStack backgroundColor="$green10" px="$1.5" br="$2">
        <Paragraph2 color="$background" bold>
          New
        </Paragraph2>
      </XStack>,
    source: "home_get_help_prompts_open_enrollment"
  },
  [PROMPT_TYPES.OTHER]: {
    title: "Something else",
    description: "We'll help you find any answer. Share what you need.",
    badge: <SomethingElseBadge />,
    source: "home_get_help_prompts_other"
  }
};
export function GetHelpPrompts() {
  const {
    pushGetHelp
  } = usePushGetHelp();
  const isOpenEnrollmentChatOptionEnabled = useBoolVariation(Flag.OPEN_ENROLLMENT_CHAT_OPTION_ENABLED, false);
  const {
    user
  } = useAuth();
  const isUserBenefitsTargeted = isBenefitsTargeted(user?.userSegmentationData?.features);
  const shouldShowUploadBill = useShouldShowUploadBill();

  // Compute the actual order of prompts that will be displayed
  const promptOrder = useMemo(() => {
    const baseOrder: PromptType[] = shouldShowUploadBill ?
    // If upload bill is shown, only show benefits question
    [PROMPT_TYPES.BENEFITS_QUESTION] :
    // Otherwise, use the existing logic
    isUserBenefitsTargeted ? [PROMPT_TYPES.BENEFITS_QUESTION, PROMPT_TYPES.BILL_ISSUE] : [PROMPT_TYPES.BILL_ISSUE, PROMPT_TYPES.BENEFITS_QUESTION];
    const fullOrder: PromptType[] = [...baseOrder, ...(isOpenEnrollmentChatOptionEnabled ? [PROMPT_TYPES.OPEN_ENROLLMENT] : []), PROMPT_TYPES.OTHER];
    return fullOrder;
  }, [isUserBenefitsTargeted, isOpenEnrollmentChatOptionEnabled, shouldShowUploadBill]);

  // Track prompt order when component renders
  useEffect(() => {
    analyticsService.trackEvent("HomeScreenCTAOrderViewed", {
      ctaOrder: promptOrder
    });
  }, [promptOrder]);
  return <WidgetWrapper title="Get help" wrapperProps={{
    px: "$0"
  }} titleProps={{
    px: "$sp2.5",
    pb: "$sp1"
  }} removeDivider data-sentry-element="WidgetWrapper" data-sentry-component="GetHelpPrompts" data-sentry-source-file="GetHelpPrompts.tsx">
      <YStack data-sentry-element="YStack" data-sentry-source-file="GetHelpPrompts.tsx">
        {promptOrder.map((promptType, index) => {
        const config = PROMPT_CONFIGS[promptType];
        return <YStack key={promptType}>
              {index > 0 ? <Separator borderColor="$strokeSecondary" mx="$sp2.5" /> : null}
              <GetHelpPrompt title={config.title} description={config.description} badge={config.badge} labelBadge={config.labelBadge} onPress={() => {
            const caseType = promptType === PROMPT_TYPES.BILL_ISSUE ? "BILL_ISSUE_V2" : promptType;
            pushGetHelp({
              caseType,
              title: config.title,
              source: config.source
            });
          }} />
            </YStack>;
      })}
      </YStack>
    </WidgetWrapper>;
}